'use client'

import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { memo, useMemo } from 'react'

import { IProductCard } from '@src/components/product-card/product-card.types'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { ProductCard } from '@src/ds/blocks/product-card'
import { SelectedFilter } from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import NoProduct from '@src/pages/[...slug]/components/collection/molecules/noProducts/NoProduct'
import {
  getTrackingMetaData,
  PRODUCT_VIEW_TRACKER,
} from '@src/pages/search/searchResultsSection/searchResults/searchResults.constants'
import { FINDING_METHOD_AGGREGATE, setFindingMethod } from '@src/telemetry/findingMethod'

const sectionShellClass = css({
  width: 'full',
  px: { base: 'space-16', lg: 'space-48' },
})

const sectionInnerClass = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: 'full',
  maxWidth: '1504px',
  marginInline: 'auto',
  pb: { base: 'space-80', lg: 'space-100' },
})

const gridClass = css({
  display: 'grid',
  width: 'full',
  gridTemplateColumns: {
    base: 'repeat(2, minmax(0, 1fr))',
    lg: 'repeat(4, minmax(0, 1fr))',
  },
  columnGap: { base: 'space-16', lg: 'space-24' },
  rowGap: { base: 'space-32', lg: 'space-48' },
})

const noResultsTitleClass = css({
  textStyle: { base: 'title-14', lg: 'title-18' },
  textAlign: 'center',
  width: 'full',
})

const noResultsBodyClass = css({
  width: 'full',
  textAlign: 'center',
  marginTop: 'space-8',
})

export interface SearchResultsContentDSProps {
  productCards: IProductCard[]
  searchQuery: string
  onResetFilters: () => void
  selectedFilters: SelectedFilter
}

// Identical for every search tile, so it lives at module scope instead of being re-created per card
// (a fresh arrow per card would defeat ProductCard's memo). Matches legacy: sets the global
// finding-method aggregate; intentionally NOT added to the tracked event context.
const handleSearchProductClick = () => {
  setFindingMethod[FINDING_METHOD_AGGREGATE.SEARCH]?.()
}

interface SearchResultProductCardProps {
  productCard: IProductCard
  index: number
  searchQuery: string
}

// Memoized per-card wrapper. The grid maps over `productCards`, so the per-card
// `productClickTrackConfig` cannot be `useMemo`'d in the loop; it's built here from stable primitive
// inputs behind a `memo` boundary so the wrapped ProductCard's shallow-compare skips re-renders on
// unrelated parent state. `index` is a dep so the tracked position stays correct if results reorder.
const SearchResultProductCard = memo(function SearchResultProductCard({
  productCard,
  index,
  searchQuery,
}: SearchResultProductCardProps) {
  const productClickTrackConfig = useMemo(
    () => ({
      trackInternal: true,
      // eslint-disable-next-line i18next/no-literal-string -- telemetry category, not user-facing copy
      category: 'search_results_collection',
      // eslint-disable-next-line i18next/no-literal-string -- telemetry action, not user-facing copy
      action: 'search_by_query',
      // Parity with the legacy click context (search-results-content-legacy.tsx):
      // searchQuery / position / product_position / getTrackingMetaData().context only.
      // `findingMethod` is intentionally NOT sent here — the global aggregate is still
      // set via `onClickCb` (matching legacy), but adding it to the event context
      // would be a net-new telemetry property vs the legacy path.
      context: {
        searchQuery,
        position: index,
        product_position: index,
        ...getTrackingMetaData().context,
      },
    }),
    [searchQuery, index],
  )

  return (
    <ProductCard
      productCard={productCard}
      tracker={PRODUCT_VIEW_TRACKER}
      productViewTracker={PRODUCT_VIEW_TRACKER}
      searchQuery={searchQuery}
      position={index + 1}
      isPageEligibleForHolidayTag
      onClickCb={handleSearchProductClick}
      productClickTrackConfig={productClickTrackConfig}
      // eslint-disable-next-line i18next/no-literal-string -- DS ProductCard variant token, not user-facing copy
      dsVariant="default"
    />
  )
})

SearchResultProductCard.displayName = 'SearchResultProductCard'

export const SearchResultsContentDS = ({
  productCards,
  searchQuery,
  onResetFilters,
  selectedFilters,
}: SearchResultsContentDSProps) => {
  const { t } = useTranslation()
  const hasAppliedFilters = SelectedFilter.isAnyFilterSelected(selectedFilters)

  if (productCards.length > 0) {
    return (
      <div className={sectionShellClass}>
        <div className={sectionInnerClass}>
          <div className={gridClass}>
            {productCards.map((productCard, pIdx) => (
              <SearchResultProductCard
                key={`${productCard.product.productId}_${pIdx}`}
                productCard={productCard}
                index={pIdx}
                searchQuery={searchQuery}
              />
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={sectionShellClass}>
      <div className={sectionInnerClass}>
        {!hasAppliedFilters && <h1 className={noResultsTitleClass}>{`"${searchQuery.toUpperCase()}"`}</h1>}
        <div className={noResultsBodyClass} data-testid={TEST_LOCATORS.NO_SEARCH_RESULTS}>
          {hasAppliedFilters ? (
            <NoProduct onResetFilters={onResetFilters} isFiltersApplied={hasAppliedFilters} />
          ) : (
            <span
              className={css({
                textStyle: 'body-14',
              })}
            >
              {t('no_search_results')}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
